import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin, closestCenter } from '@dnd-kit/core';
import CardRowOverlay from './CardRowOverlay';
import GroupSection from './GroupSection';
import GroupDropSlot from './GroupDropSlot';
import AddColumnModal from './AddColumnModal';
import SelectionBar from './SelectionBar';
import useBoardStore from '../../store/boardStore';
import useToastStore from '../../store/toastStore';
import useBoardCan from '../../hooks/useBoardCan';
import { formatBRL } from '../../utils/currency';

// Referencia estavel para grupos sem cards (evita array novo a cada render).
const EMPTY_CARDS = [];

// Preserva a referencia do array anterior quando ele contem exatamente os mesmos
// objetos card, na mesma ordem (comparacao por identidade — moveCardLocal preserva
// os objetos dos grupos intocados via `return c`). Isso mantem estaveis os arrays
// de cards dos grupos nao afetados por um move, fazendo o React.memo do GroupSection
// e o memo do CardRow segurarem. Mesma ideia de reconcileListById do boardStore.
function reconcileCardArray(prev, next) {
  if (!prev || prev.length !== next.length) return next;
  for (let i = 0; i < next.length; i++) {
    if (prev[i] !== next[i]) return next;
  }
  return prev;
}

export default function BoardTable({ filters, hiddenColumns, sort, groupBy }) {
  // Selectors granulares: cada hook se inscreve apenas na fatia que usa.
  // Actions tem referencia estavel no Zustand — selecionar via selector individual
  // nao causa re-render extra.
  const currentBoard = useBoardStore((s) => s.currentBoard);
  const groups = useBoardStore((s) => s.groups);
  const columns = useBoardStore((s) => s.columns);
  const cards = useBoardStore((s) => s.cards);
  const cardValues = useBoardStore((s) => s.cardValues);
  const statusOptions = useBoardStore((s) => s.statusOptions);
  const users = useBoardStore((s) => s.users);
  const selectedCardIds = useBoardStore((s) => s.selectedCardIds);
  const createColumn = useBoardStore((s) => s.createColumn);
  const createGroup = useBoardStore((s) => s.createGroup);
  const moveCard = useBoardStore((s) => s.moveCard);
  const moveCardLocal = useBoardStore((s) => s.moveCardLocal);
  const moveCardsBatch = useBoardStore((s) => s.moveCardsBatch);
  const moveCardsBatchAsync = useBoardStore((s) => s.moveCardsBatchAsync);
  const reorderColumns = useBoardStore((s) => s.reorderColumns);
  const reorderGroups = useBoardStore((s) => s.reorderGroups);
  const toggleCardSelection = useBoardStore((s) => s.toggleCardSelection);
  const selectCardGroup = useBoardStore((s) => s.selectCardGroup);
  const clearCardSelection = useBoardStore((s) => s.clearCardSelection);
  const showToast = useToastStore((s) => s.show);
  // "Novo Grupo" e estrutural — mesma fonte de permissao do GroupSection
  // (edit_structure), com fallback identico a hoje quando my_access nao vier.
  const canEditStructure = useBoardCan().edit_structure;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [activeCard, setActiveCard] = useState(null);
  const [activeGroupColor, setActiveGroupColor] = useState(null);
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [pressedGroupId, setPressedGroupId] = useState(null);
  const [multiDragCount, setMultiDragCount] = useState(0);
  const cardsSnapshotRef = useRef(null);
  const lastDragTargetRef = useRef(null);
  // Guarda o Map<groupId, Card[]> do render anterior para reconciliar referencias
  // de array por grupo (so o grupo tocado por um move recebe array novo).
  const prevGroupCardsRef = useRef(new Map());
  // Throttle do moveCardLocal: agenda o move otimista no maximo 1x/frame (rAF).
  // pendingMoveRef guarda os args do ultimo alvo; rafIdRef segura o handle do rAF.
  const pendingMoveRef = useRef(null);
  const rafIdRef = useRef(null);

  // Collision detection barata: pointerWithin faz hit-test e retorna cedo;
  // so cai em closestCenter (1 ponto, mais barato que os 4 cantos de closestCorners)
  // quando o ponteiro esta fora de qualquer droppable. Mesmo padrao do AppLayout.
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
  }, []);

  // Aplica imediatamente o move pendente (se houver) e zera o agendamento.
  // Usado tanto pelo rAF quanto pelo FLUSH sincrono no inicio do handleDragEnd —
  // garante que o ultimo alvo do drag seja refletido no store antes do drop
  // (critico para nao regredir o fix cross-group do commit 34c7428).
  const flushPendingMove = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const pending = pendingMoveRef.current;
    if (pending) {
      pendingMoveRef.current = null;
      moveCardLocal(pending.cardId, pending.container, pending.pos);
    }
  }, [moveCardLocal]);

  // Cancela o rAF e descarta o move pendente sem aplicar (cancel/unmount).
  const cancelPendingMove = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingMoveRef.current = null;
  }, []);

  // Cleanup defensivo: nunca deixar rAF pendente apos desmontar.
  useEffect(() => () => cancelPendingMove(), [cancelPendingMove]);

  useEffect(() => {
    if (!pressedGroupId) return;
    const release = () => setPressedGroupId(null);
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };
  }, [pressedGroupId]);

  const handleDragStart = (event) => {
    const { active } = event;
    const t = active.data.current?.type;
    lastDragTargetRef.current = null;
    if (t === 'group') {
      setDraggingGroupId(active.data.current.groupId);
      useBoardStore.getState().setDragging(true);
      return;
    }
    if (t !== 'card') return;
    useBoardStore.getState().setDragging(true);
    const card = cards.find((c) => c.id === active.data.current.cardId);
    const group = groups.find((g) => g.id === card?.group_id);
    setActiveCard(card || null);
    setActiveGroupColor(group?.color || null);
    cardsSnapshotRef.current = useBoardStore.getState().cards;
    const storeSelectedIds = useBoardStore.getState().selectedCardIds;
    const activeCardId = Number(active.data.current.cardId);
    const isMulti = storeSelectedIds.length > 1 && storeSelectedIds.map(Number).includes(activeCardId);
    setMultiDragCount(isMulti ? storeSelectedIds.length : 0);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeData = active.data?.current;
    const overData = over.data?.current;
    if (activeData?.type !== 'card') return;

    const activeCardId = activeData.cardId;
    const state = useBoardStore.getState();
    const activeCardState = state.cards.find((c) => c.id === activeCardId);
    if (!activeCardState) return;

    let overContainer = null;
    let overCardId = null;
    if (overData?.type === 'card') {
      overContainer = overData.groupId;
      overCardId = overData.cardId;
    } else if (overData?.type === 'group') {
      overContainer = overData.groupId;
    } else {
      return;
    }

    if (activeCardState.group_id === overContainer) return;

    const destCards = state.cards
      .filter((c) => c.group_id === overContainer && c.id !== activeCardId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    let targetPos = destCards.length;
    if (overCardId) {
      const idx = destCards.findIndex((c) => c.id === overCardId);
      if (idx >= 0) targetPos = idx;
    }

    // Evita chamar moveCardLocal quando o destino não mudou (reduz re-renders durante hover)
    const last = lastDragTargetRef.current;
    if (last && last.container === overContainer && last.pos === targetPos && last.overCardId === overCardId) return;
    lastDragTargetRef.current = { container: overContainer, pos: targetPos, overCardId };

    // Throttle via rAF: guarda o ultimo alvo e aplica moveCardLocal no maximo
    // 1x/frame. Se ja ha rAF agendado, apenas atualiza pendingMoveRef (coalesce).
    pendingMoveRef.current = { cardId: activeCardId, container: overContainer, pos: targetPos };
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const pending = pendingMoveRef.current;
        if (pending) {
          pendingMoveRef.current = null;
          moveCardLocal(pending.cardId, pending.container, pending.pos);
        }
      });
    }
  };

  const handleDragEnd = async (event) => {
    // FLUSH sincrono do move pendente ANTES de qualquer leitura do estado live.
    // O throttle (rAF) pode ter o ultimo alvo so em pendingMoveRef; sem este flush,
    // movedAcrossGroup (le live x snapshot) perderia o ultimo move cross-group e
    // regrediria o fix do commit 34c7428. Aplica e cancela o rAF.
    flushPendingMove();
    useBoardStore.getState().setDragging(false);
    lastDragTargetRef.current = null;
    setActiveCard(null);
    setActiveGroupColor(null);
    setMultiDragCount(0);
    const { active, over } = event;
    const activeData = active.data.current;
    const overData = over?.data?.current;

    if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd START:', { activeId: active?.id, overId: over?.id, activeDataType: active?.data?.current?.type, selectedCount: selectedCardIds?.length });

    if (activeData?.type === 'group' && currentBoard) {
      setDraggingGroupId(null);
      if (!over) {
        if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 138:', { over, activeId: active?.id });
        return;
      }
      if (overData?.type !== 'groupSlot') return;
      const sorted = [...groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const oldIdx = sorted.findIndex((g) => g.id === activeData.groupId);
      if (oldIdx === -1) return;
      let newIdx = overData.index;
      if (newIdx > oldIdx) newIdx -= 1;
      if (newIdx === oldIdx) return;
      const next = [...sorted];
      const [moved] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, moved);
      try { await reorderGroups(currentBoard.id, next.map((g) => g.id)); }
      catch (err) { console.error('reorderGroups failed:', err); }
      return;
    }

    if (!over) {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 159 (no over):', { activeId: active?.id });
      return;
    }
    const isMultiDragNow = activeData?.type === 'card' && Array.isArray(selectedCardIds) && selectedCardIds.length > 1 && selectedCardIds.includes(Number(activeData.cardId));
    // Cross-group: o handleDragOver já moveu o card para o grupo destino no state
    // otimista. Ao soltar sobre o próprio card arrastado, over.id === active.id,
    // mas o group_id atual (live) difere do original (snapshot) → NÃO abortar,
    // seguir para o cálculo de targetGroupId/targetPosition e disparar moveCard (PUT).
    const movedAcrossGroup = (() => {
      if (activeData?.type !== 'card') return false;
      const activeCardIdNum = Number(activeData.cardId);
      const originalGroupId = cardsSnapshotRef.current?.find((c) => c.id === activeCardIdNum)?.group_id;
      if (originalGroupId == null) return false;
      const liveGroupId = useBoardStore.getState().cards.find((c) => c.id === activeCardIdNum)?.group_id;
      return liveGroupId != null && liveGroupId !== originalGroupId;
    })();
    if (!isMultiDragNow && !movedAcrossGroup && active.id === over.id) {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 164 (same id, single drag):', { activeId: active?.id, overId: over?.id });
      return;
    }

    if (activeData?.type === 'column' && overData?.type === 'column' && currentBoard) {
      const sorted = [...columns].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const oldIdx = sorted.findIndex((c) => c.id === activeData.columnId);
      const newIdx = sorted.findIndex((c) => c.id === overData.columnId);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      const next = [...sorted];
      const [moved] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, moved);
      try { await reorderColumns(currentBoard.id, next.map((c) => c.id)); }
      catch (err) { console.error('reorderColumns failed:', err); }
      return;
    }

    if (activeData?.type !== 'card') {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 177:', { activeDataType: activeData?.type, activeData });
      return;
    }

    const activeCardId = activeData.cardId;
    const originalCards = cardsSnapshotRef.current;
    cardsSnapshotRef.current = null;
    const restoreSnapshot = () => {
      if (originalCards) useBoardStore.setState({ cards: originalCards });
    };

    const liveCards = useBoardStore.getState().cards;
    const activeCard = liveCards.find((c) => c.id === activeCardId);
    if (!activeCard) {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 180:', { activeCardId, liveCardsCount: liveCards.length });
      return;
    }

    let targetGroupId = null;
    let targetPosition = null;

    if (overData?.type === 'card') {
      targetGroupId = overData.groupId;
      const originalGroupId = originalCards?.find((c) => c.id === activeCardId)?.group_id;
      const isCrossGroup = originalGroupId !== targetGroupId;
      const sortedGroupCards = liveCards
        .filter((c) => c.group_id === targetGroupId && (isCrossGroup ? c.id !== activeCardId : true))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      const oldIdx = sortedGroupCards.findIndex((c) => c.id === activeCardId);
      const overIdx = sortedGroupCards.findIndex((c) => c.id === overData.cardId);
      if (overIdx === -1) {
        targetPosition = sortedGroupCards.length;
      } else if (oldIdx === overIdx) {
        const wasSameAsOrigin = originalCards?.find((c) => c.id === activeCardId)?.group_id === targetGroupId;
        if (wasSameAsOrigin) {
          if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 209:', { activeCardId, targetGroupId, oldIdx, overIdx, wasSameAsOrigin });
          restoreSnapshot();
          return;
        }
        targetPosition = overIdx;
      } else {
        targetPosition = overIdx;
      }
    } else if (overData?.type === 'group') {
      targetGroupId = overData.groupId;
      const sortedGroupCards = liveCards
        .filter((c) => c.group_id === targetGroupId && c.id !== activeCardId)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      targetPosition = sortedGroupCards.length;
    } else {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 210 (overData type unknown):', { overDataType: overData?.type, overData, activeId: active?.id, overId: over?.id });
      restoreSnapshot();
      return;
    }

    if (targetGroupId == null || targetPosition == null) {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 214:', { targetGroupId, targetPosition, overDataType: overData?.type });
      restoreSnapshot();
      return;
    }

    const storeState = useBoardStore.getState();
    const storeSelectedIds = storeState.selectedCardIds;
    const isMultiDrag =
      storeSelectedIds.length > 1 && storeSelectedIds.includes(activeCardId);

    if (isMultiDrag) {
      // Ordena os cards selecionados pela posição visual atual (ordem relativa original)
      const liveCardsNow = storeState.cards;
      const selectedOrdered = storeSelectedIds
        .map((id) => liveCardsNow.find((c) => c.id === id))
        .filter(Boolean)
        .sort((a, b) => {
          // Primeiro agrupa por group (mesma ordem visual do board), depois por position
          if (a.group_id !== b.group_id) return (a.group_id ?? 0) - (b.group_id ?? 0);
          return (a.position ?? 0) - (b.position ?? 0);
        })
        .map((c) => c.id);

      // Captura estado original de cada card selecionado para undo
      const undoData = selectedOrdered.map((id) => {
        const orig = originalCards.find((c) => c.id === id);
        return { id, groupId: orig.group_id, position: orig.position };
      });

      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd CALLING STORE ACTION:', { action: 'moveCardsBatch', selectedOrdered, targetGroupId, targetPosition, selectedCount: selectedOrdered.length });
      // Otimista local em batch
      storeState.moveCardsBatch(selectedOrdered, targetGroupId, targetPosition);

      // PUTs em paralelo com rollback se falhar
      storeState.moveCardsBatchAsync(selectedOrdered, targetGroupId, targetPosition, originalCards)
        .then(() => {
          showToast({
            message: `${selectedOrdered.length} itens movidos`,
            onUndo: async () => {
              try {
                const byGroup = new Map();
                for (const u of undoData) {
                  if (!byGroup.has(u.groupId)) byGroup.set(u.groupId, []);
                  byGroup.get(u.groupId).push(u);
                }
                const state = useBoardStore.getState();
                for (const [groupId, items] of byGroup.entries()) {
                  const sorted = items.sort((a, b) => a.position - b.position);
                  const ids = sorted.map((i) => i.id);
                  const startPos = sorted[0].position;
                  state.moveCardsBatch(ids, groupId, startPos);
                  await state.moveCardsBatchAsync(ids, groupId, startPos, state.cards);
                }
              } catch (err) {
                console.error('Undo batch move failed:', err);
              }
            },
          });
        })
        .catch((err) => console.error('moveCardsBatch failed:', err));

      // Mantém seleção (não limpa)
      return;
    }

    const originalCardSnapshot = originalCards?.find((c) => c.id === activeCardId);
    const movedToNewPlace =
      originalCardSnapshot &&
      (originalCardSnapshot.group_id !== targetGroupId ||
        originalCardSnapshot.position !== targetPosition);

    if (!movedToNewPlace) {
      if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd EARLY RETURN line 281 (not moved):', { activeCardId, targetGroupId, targetPosition, originalGroupId: originalCardSnapshot?.group_id, originalPosition: originalCardSnapshot?.position, hasSnapshot: !!originalCardSnapshot });
      // restaura snapshot pra evitar drift do moveCardLocal incremental
      restoreSnapshot();
      return;
    }

    if (window.__DEBUG_DRAG__) console.debug('[drag] handleDragEnd CALLING STORE ACTION:', { action: 'moveCard', activeCardId, targetGroupId, targetPosition });
    try {
      // moveCard do store: aplica local + PUT + protege via pendingCardMoves
      await useBoardStore.getState().moveCard(activeCardId, targetGroupId, targetPosition);
      showToast({
        message: 'Item movido',
        onUndo: async () => {
          try {
            await useBoardStore.getState().moveCard(
              activeCardId,
              originalCardSnapshot.group_id,
              originalCardSnapshot.position,
            );
          } catch (err) {
            console.error('Undo move failed:', err);
          }
        },
      });
    } catch (err) {
      console.error('moveCard failed:', err);
      restoreSnapshot();
    }
  };
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const selectedIds = useMemo(() => new Set(selectedCardIds), [selectedCardIds]);

  useEffect(() => { clearCardSelection(); }, [currentBoard?.id, clearCardSelection]);

  const toggleSelect = toggleCardSelection;
  const toggleSelectGroup = selectCardGroup;
  const clearSelection = clearCardSelection;
  // Callback estavel para o GroupSection (setShowAddColumn e estavel; evita
  // recriar a arrow a cada render e invalidar o React.memo do GroupSection).
  const openAddColumn = useCallback(() => setShowAddColumn(true), []);

  const GROUP_COLORS = ['#579bfc', '#e2445c', '#00c875', '#fdab3d', '#a25ddc', '#0086c0', '#ff642e', '#cab641'];

  const sortedGroups = useMemo(() => [...groups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)), [groups]);
  const sortedColumns = useMemo(() => [...columns].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)), [columns]);
  const visibleColumns = useMemo(
    () => sortedColumns.filter((c) => !hiddenColumns?.has(c.id)),
    [sortedColumns, hiddenColumns]
  );
  const titleColWidth = currentBoard?.title_col_width ?? 280;
  const overlayTotalWidth = titleColWidth + visibleColumns.reduce((s, c) => s + (c.width ?? 150), 0) + 40;

  const f = filters || { search: '', person: null, status: {} };
  const searchLower = (f.search || '').toLowerCase().trim();
  const hasSearch = searchLower.length > 0;

  // M3c, item 3: resolve o valor bruto de uma célula de status para a
  // status_option real, pelo id (formato canônico {id,label} — StatusCell.jsx
  // grava assim desde M3c) com fallback para o label cru (valor antigo ainda
  // não convertido, ou o próprio option.id já foi excluído). Retorna null
  // quando não há valor ou nenhuma opção corresponde (etiqueta órfã "solta").
  const resolveStatusOption = (colId, v) => {
    const opts = statusOptions[colId] || [];
    if (v && typeof v === 'object' && v.id != null) {
      const byId = opts.find((o) => o.id === v.id);
      if (byId) return byId;
    }
    const label = v && typeof v === 'object' ? (v.label ?? null) : (typeof v === 'string' ? v : null);
    if (label == null) return null;
    return opts.find((o) => o.label === label) || null;
  };

  const cardMatchesSearch = (card) => {
    if (!hasSearch) return true;
    if (card.title?.toLowerCase().includes(searchLower)) return true;
    for (const col of columns) {
      const v = cardValues[`${card.id}:${col.id}`];
      if (v == null) continue;
      if (col.type === 'person') {
        const u = users.find((x) => x.id === v);
        if (u?.name?.toLowerCase().includes(searchLower)) return true;
      } else if (col.type === 'status' || col.type === 'select') {
        const label = v?.label || (typeof v === 'string' ? v : null);
        if (label?.toLowerCase().includes(searchLower)) return true;
      } else if (col.type === 'link') {
        const url = typeof v === 'string' ? v : v?.url;
        const label = typeof v === 'object' ? v?.label : null;
        if (url?.toLowerCase().includes(searchLower)) return true;
        if (label?.toLowerCase().includes(searchLower)) return true;
      } else if (col.type === 'currency' && typeof v === 'number') {
        // A célula mostra "R$ 1.234,56": a busca acha pelo que está na tela e também pelo número cru.
        const raw = String(v);
        if (formatBRL(v, col.config?.decimals).toLowerCase().includes(searchLower)
          || raw.includes(searchLower) || raw.replace('.', ',').includes(searchLower)) return true;
      } else if (typeof v === 'string') {
        if (v.toLowerCase().includes(searchLower)) return true;
      } else if (typeof v === 'number') {
        if (String(v).includes(searchLower)) return true;
      } else if (typeof v === 'object') {
        if (JSON.stringify(v).toLowerCase().includes(searchLower)) return true;
      }
    }
    return false;
  };

  const matchesOtherFilters = (card) => {
    if (f.person) {
      const personCol = columns.find((c) => c.type === 'person');
      if (personCol) {
        const assigned = cardValues[`${card.id}:${personCol.id}`];
        if (assigned !== f.person) return false;
      } else {
        if (card.created_by !== f.person) return false;
      }
    }
    if (f.status) {
      for (const [colId, ids] of Object.entries(f.status)) {
        if (!ids || ids.length === 0) continue;
        const v = cardValues[`${card.id}:${colId}`];
        const opt = resolveStatusOption(Number(colId), v);
        if (!opt || !ids.includes(opt.id)) return false;
      }
    }
    return true;
  };

  const groupNameMatches = (name) =>
    hasSearch && typeof name === 'string' && name.toLowerCase().includes(searchLower);

  const sortCards = (arr) => {
    if (!sort?.columnId) return [...arr].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col) return arr;
    const getKey = (card) => {
      if (col.type === 'created') return card.created_at || '';
      if (col.type === 'updated') return card.updated_at || '';
      const v = cardValues[`${card.id}:${col.id}`];
      // Moeda ordena como número (como texto, "1000" viria antes de "200"); sem valor vem primeiro no asc, como nos outros tipos.
      if (col.type === 'currency') return typeof v === 'number' ? v : -Infinity;
      if (v == null) return '';
      if (col.type === 'person') return users.find((u) => u.id === v)?.name || '';
      if (col.type === 'date') return typeof v === 'string' ? v : (v.start || '');
      if (col.type === 'status') {
        const opt = resolveStatusOption(col.id, v);
        return opt?.label || v?.label || (typeof v === 'string' ? v : '');
      }
      if (typeof v === 'object') return v.label || v.url || JSON.stringify(v);
      return String(v);
    };
    return [...arr].sort((a, b) => {
      const ka = getKey(a), kb = getKey(b);
      if (ka < kb) return sort.dir === 'asc' ? -1 : 1;
      if (ka > kb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredCards = useMemo(
    () => cards.filter((c) => matchesOtherFilters(c) && cardMatchesSearch(c)),
    [cards, f, cardValues, columns, users]
  );

  // Particiona os cards por grupo (filtrados + ordenados) UMA vez por render e
  // reconcilia cada array contra o render anterior: grupos nao tocados por um move
  // preservam a MESMA referencia de array, deixando o React.memo do GroupSection
  // pular o re-render. Respeita filtros (matchesOtherFilters/cardMatchesSearch),
  // busca (nameHit) e ordenacao (sortCards) — mesmo comportamento do renderGroups.
  const groupCardsMap = useMemo(() => {
    const prev = prevGroupCardsRef.current;
    const next = new Map();
    for (const group of sortedGroups) {
      const nameHit = groupNameMatches(group.name);
      const pool = cards
        .filter((c) => c.group_id === group.id)
        .filter(matchesOtherFilters);
      const matched = nameHit ? pool : pool.filter(cardMatchesSearch);
      const sorted = sortCards(matched);
      next.set(group.id, reconcileCardArray(prev.get(group.id), sorted));
    }
    prevGroupCardsRef.current = next;
    return next;
    // Deps subjacentes das closures (filtros/busca/sort) acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, sortedGroups, f, cardValues, columns, users, sort, hasSearch, searchLower]);

  const renderGroups = () => {
    if (!groupBy || groupBy === 'group') {
      const activeGroupId = draggingGroupId ?? pressedGroupId;
      const isDraggingGroup = activeGroupId != null;
      const showDropSlots = draggingGroupId != null;
      const nodes = [];
      sortedGroups.forEach((group, idx) => {
        const nameHit = groupNameMatches(group.name);
        // Array ja filtrado/ordenado e com referencia estavel (groupCardsMap).
        const matchedCards = groupCardsMap.get(group.id) ?? EMPTY_CARDS;
        if (hasSearch && !nameHit && matchedCards.length === 0) return;
        if (showDropSlots) {
          nodes.push(<GroupDropSlot key={`slot-${idx}`} index={idx} />);
        }
        nodes.push(
          <div key={group.id} className="group/group">
            <GroupSection
              group={group}
              columns={visibleColumns}
              cards={matchedCards}
              onAddColumn={openAddColumn}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectGroup={toggleSelectGroup}
              draggable
              forceCollapsed={isDraggingGroup}
              compact={isDraggingGroup}
              onGripPress={setPressedGroupId}
            />
          </div>
        );
      });
      if (showDropSlots) {
        nodes.push(<GroupDropSlot key={`slot-end`} index={sortedGroups.length} />);
      }
      return nodes;
    }

    const match = groupBy.match(/^col:(\d+)$/);
    if (!match) return null;
    const colId = Number(match[1]);
    const col = columns.find((c) => c.id === colId);
    if (!col) return null;

    const buckets = new Map();
    const addTo = (key, card, meta) => {
      if (!buckets.has(key)) buckets.set(key, { meta, cards: [] });
      buckets.get(key).cards.push(card);
    };

    for (const card of filteredCards) {
      const v = cardValues[`${card.id}:${colId}`];
      if (col.type === 'status') {
        const opt = resolveStatusOption(colId, v);
        const rawLabel = v && typeof v === 'object' ? (v.label ?? null) : (typeof v === 'string' ? v : null);
        if (!opt && !rawLabel) { addTo('__empty__', card, { name: 'Sem status', color: '#9699a6' }); continue; }
        // Chave por id quando resolvido (evita colisão de dois labels iguais
        // em opções diferentes — status_options não tem UNIQUE(column_id,label));
        // cai para o label cru só quando a etiqueta já foi excluída (órfã).
        const key = opt ? `id:${opt.id}` : `label:${rawLabel}`;
        addTo(key, card, { name: opt?.label || rawLabel, color: opt?.color || '#9699a6' });
      } else if (col.type === 'person') {
        if (!v) { addTo('__empty__', card, { name: 'Não atribuído', color: '#9699a6' }); continue; }
        const u = users.find((x) => x.id === v);
        addTo(String(v), card, { name: u?.name || `Usuário ${v}`, color: '#579bfc' });
      }
    }

    return [...buckets.entries()].map(([key, { meta, cards: bCards }]) => {
      const pseudoGroup = { id: `pseudo:${key}`, name: meta.name, color: meta.color };
      return (
        <div key={key} className="group/group">
          <GroupSection
            group={pseudoGroup}
            columns={visibleColumns}
            cards={sortCards(bCards)}
            onAddColumn={openAddColumn}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectGroup={toggleSelectGroup}
          />
        </div>
      );
    });
  };

  const handleCreateColumn = async (name, type, config = {}) => {
    if (!currentBoard) return;
    await createColumn(currentBoard.id, name, type, config);
  };

  const handleCreateGroup = async () => {
    if (!currentBoard) return;
    const name = newGroupName.trim();
    if (!name) return;
    const color = GROUP_COLORS[groups.length % GROUP_COLORS.length];
    await createGroup(currentBoard.id, name, color);
    setNewGroupName('');
    setShowNewGroup(false);
  };

  const isGroupingByColumn = groupBy && groupBy !== 'group';

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        // Descarta o move pendente sem aplicar e cancela o rAF — o snapshot
        // restaura o estado original, entao nao queremos o ultimo move otimista.
        cancelPendingMove();
        useBoardStore.getState().setDragging(false);
        lastDragTargetRef.current = null;
        if (cardsSnapshotRef.current) useBoardStore.setState({ cards: cardsSnapshotRef.current });
        cardsSnapshotRef.current = null;
        setDraggingGroupId(null);
        setPressedGroupId(null);
        setActiveCard(null);
        setActiveGroupColor(null);
        setMultiDragCount(0);
      }}
    >
    <div className="h-full w-full flex">
    <div className="w-8 shrink-0" aria-hidden="true" />
    <div className="flex-1 h-full overflow-auto board-scroll">
      <div className="min-w-max py-6 pr-8">
        {renderGroups()}

        {!isGroupingByColumn && canEditStructure && (
          showNewGroup ? (
            <div className="flex items-center gap-2 mt-2 px-4">
              <input
                autoFocus
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateGroup();
                  if (e.key === 'Escape') {
                    setShowNewGroup(false);
                    setNewGroupName('');
                  }
                }}
                onBlur={() => {
                  if (!newGroupName.trim()) {
                    setShowNewGroup(false);
                    setNewGroupName('');
                  }
                }}
                placeholder="Nome do grupo..."
                className="bg-card border border-border rounded px-3 py-2 text-sm text-text-primary placeholder-text-secondary outline-none focus:border-accent/50 w-64"
              />
            </div>
          ) : (
            <button
              onClick={() => setShowNewGroup(true)}
              className="flex items-center gap-2 mt-2 px-4 py-2.5 text-sm text-text-secondary hover:text-accent hover:bg-white/5 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Novo Grupo
            </button>
          )
        )}
      </div>

      {showAddColumn && (
        <AddColumnModal
          onClose={() => setShowAddColumn(false)}
          onCreate={handleCreateColumn}
        />
      )}

      <SelectionBar selectedIds={selectedIds} onClear={clearSelection} hiddenColumns={hiddenColumns} />
    </div>
    </div>
    <DragOverlay dropAnimation={null}>
      {activeCard && multiDragCount > 1 ? (
        <div style={{ position: 'relative', width: overlayTotalWidth }}>
          <CardRowOverlay
            card={activeCard}
            columns={visibleColumns}
            groupColor={activeGroupColor}
            cardValues={cardValues}
            statusOptions={statusOptions}
            users={users}
            titleColWidth={titleColWidth}
          />
          <div
            className="min-w-[26px] h-6 px-2 rounded-full bg-accent text-white text-xs font-bold flex items-center justify-center shadow-lg"
            style={{ position: 'absolute', top: '-8px', left: '-8px', zIndex: 2 }}
          >
            {multiDragCount}
          </div>
        </div>
      ) : activeCard ? (
        <CardRowOverlay
          card={activeCard}
          columns={visibleColumns}
          groupColor={activeGroupColor}
          cardValues={cardValues}
          statusOptions={statusOptions}
          users={users}
          titleColWidth={titleColWidth}
        />
      ) : null}
      {draggingGroupId && (() => {
        const g = groups.find((x) => x.id === draggingGroupId);
        if (!g) return null;
        return (
          <div
            className="flex items-center gap-2 px-3 py-2 bg-card border rounded-lg shadow-2xl"
            style={{ borderColor: g.color, borderLeftWidth: 4 }}
          >
            <span className="group-title-text font-bold" style={{ color: g.color }}>
              {g.name}
            </span>
            <span className="text-[13px] text-text-secondary">
              {cards.filter((c) => c.group_id === g.id).length} itens
            </span>
          </div>
        );
      })()}
    </DragOverlay>
    </DndContext>
  );
}
